import { clearInvitePasswordPendingHint } from '../lib/authInvitePasswordPending'
import {
  klantSignupEmailRedirectUrl,
  zakelijkSignupEmailRedirectUrl,
} from '../lib/authEmailRedirects'
import { isGeldigKlantTelefoon } from '../lib/klantProfielHelpers'
import { supabase } from './supabase'
import type { Session, User, AuthChangeEvent } from '@supabase/supabase-js'

/** Resultaat van signUp: bij verplichte e-mailbevestiging is session vaak null. */
export type AuthSignUpData = {
  user: User | null
  session: Session | null
}

export type RegisterResult = AuthSignUpData & {
  needsEmailConfirmation: boolean
  likelyAlreadyRegistered: boolean
}

/** Supabase anti-enumeratie: fake user zonder identities bij al bevestigd adres. */
export function isSignupLikelyAlreadyRegistered (data: AuthSignUpData): boolean {
  if (!data.user || data.session) return false
  const identities = data.user.identities
  return !identities || identities.length === 0
}

export function isSignupPendingEmailConfirmation (data: AuthSignUpData): boolean {
  return Boolean(data.user && !data.session && !isSignupLikelyAlreadyRegistered(data))
}

export function buildRegisterResult (data: AuthSignUpData): RegisterResult {
  const likelyAlreadyRegistered = isSignupLikelyAlreadyRegistered(data)
  return {
    ...data,
    likelyAlreadyRegistered,
    needsEmailConfirmation: isSignupPendingEmailConfirmation(data),
  }
}

function throwAuthRateLimitError (msg: string): never {
  if (
    msg.toLowerCase().includes('too many') ||
    msg.toLowerCase().includes('rate limit')
  ) {
    throw new Error('Te veel pogingen. Wacht ongeveer een minuut en probeer het opnieuw.')
  }
  throw new Error(msg)
}

function isAuthRateLimitError (error: { message?: string; status?: number }): boolean {
  const msg = error.message ?? ''
  const status = error.status
  return status === 429 || msg.toLowerCase().includes('too many') || msg.toLowerCase().includes('rate limit')
}

export async function login(email: string, wachtwoord: string) {
  if (!email?.trim()) throw new Error('E-mail is verplicht')
  if (!wachtwoord) throw new Error('Wachtwoord is verplicht')
  const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: wachtwoord })
  if (error) {
    const msg = error.message || 'Inloggen mislukt'
    if (msg.toLowerCase().includes('invalid') && msg.toLowerCase().includes('credential')) {
      throw new Error('Ongeldige e-mail of wachtwoord.')
    }
    if (msg.toLowerCase().includes('email not confirmed') || msg.toLowerCase().includes('confirm your')) {
      throw new Error('Bevestig eerst je e-mail via de link in je inbox.')
    }
    throw new Error(msg)
  }
  return data
}

/** Meegestuurd in user_metadata (account_kind) en gebruikt voor app_profiles.role na signup. */
export type RegisterAccountKind = 'salon_owner' | 'klant'

export interface RegisterOptions {
  voornaam?: string
  achternaam?: string
  telefoon?: string
  /** Zakelijk (salon) vs klantportal; bepaalt app_profiles.role na registratie. */
  accountKind?: RegisterAccountKind
  /** Waar de gebruiker na e-mailbevestiging naartoe gaat. Standaard: onboarding voor salon-eigenaren. */
  emailRedirectTo?: string
}

export async function register(email: string, wachtwoord: string, options?: RegisterOptions) {
  if (!email?.trim()) throw new Error('E-mail is verplicht')
  if (!wachtwoord || wachtwoord.length < 8) throw new Error('Wachtwoord moet minimaal 8 tekens zijn')
  const accountKind: RegisterAccountKind = options?.accountKind ?? 'klant'
  const redirectTo =
    options?.emailRedirectTo ??
    (accountKind === 'salon_owner' ? zakelijkSignupEmailRedirectUrl() : klantSignupEmailRedirectUrl())
  if (accountKind === 'klant') {
    if (!options?.telefoon?.trim()) {
      throw new Error('GSM / telefoon is verplicht.')
    }
    if (!isGeldigKlantTelefoon(options.telefoon)) {
      throw new Error('Vul een geldig gsm- of telefoonnummer in (bijv. 0470 00 00 00).')
    }
  }
  const hasNameMeta = Boolean(options?.voornaam?.trim() || options?.achternaam?.trim() || options?.telefoon?.trim())
  const userData =
    hasNameMeta || accountKind !== 'klant'
      ? {
          ...(hasNameMeta
            ? {
                voornaam: options?.voornaam?.trim() || '',
                achternaam: options?.achternaam?.trim() || '',
                telefoon: options?.telefoon?.trim() || ''
              }
            : {}),
          account_kind: accountKind
        }
      : undefined

  const { data, error } = await supabase.auth.signUp({
    email: email.trim(),
    password: wachtwoord,
    options: {
      emailRedirectTo: redirectTo,
      data: userData
    }
  })
  if (error) {
    const msg = error.message || 'Registreren mislukt'
    if (isAuthRateLimitError(error as { message?: string; status?: number })) {
      throw new Error('Te veel pogingen. Wacht ongeveer een minuut en probeer het opnieuw.')
    }
    if (msg.toLowerCase().includes('signup') && msg.toLowerCase().includes('disabled')) {
      throw new Error('Registreren is uitgeschakeld. Schakel in het Supabase-dashboard onder Authentication → Providers → Email "Enable Sign Up" in.')
    }
    if (
      msg.toLowerCase().includes('already registered') ||
      msg.toLowerCase().includes('user already registered') ||
      msg.toLowerCase().includes('email already in use')
    ) {
      throw new Error(
        'Dit e-mailadres is al geregistreerd. Log in via de salonportal (/login) of via Mijn boekingen (/klant/login) om te bepalen waar je account hoort.'
      )
    }
    throw new Error(msg)
  }
  return buildRegisterResult(data as AuthSignUpData)
}

/** Opnieuw bevestigingsmail sturen na registratie (Supabase Auth, geen Resend). */
export async function resendSignupConfirmationEmail (
  email: string,
  emailRedirectTo?: string
): Promise<void> {
  const trimmed = email.trim()
  if (!trimmed) throw new Error('E-mail is verplicht')
  const redirectTo = emailRedirectTo ?? klantSignupEmailRedirectUrl()
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: trimmed,
    options: { emailRedirectTo: redirectTo },
  })
  if (error) {
    const msg = error.message || 'Kon bevestigingsmail niet opnieuw sturen'
    if (isAuthRateLimitError(error as { message?: string; status?: number })) {
      throwAuthRateLimitError(msg)
    }
    throw new Error(msg)
  }
}

async function setPasswordAndClearInvitePending (password: string) {
  if (!password || password.length < 8) {
    throw new Error('Wachtwoord moet minimaal 8 tekens zijn')
  }
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Niet ingelogd')
  const nextMeta = {
    ...(user.user_metadata ?? {}),
    invite_password_pending: false,
  }
  const { error } = await supabase.auth.updateUser({
    password,
    data: nextMeta,
  })
  if (error) {
    throw new Error(error.message ?? 'Wachtwoord opslaan mislukt')
  }
}

/** Na team-uitnodiging: wachtwoord zetten en pending-flag in user_metadata uitzetten. */
export async function setPasswordAsInvitedStaff (password: string) {
  await setPasswordAndClearInvitePending(password)
}

/** Na klant-uitnodiging of wachtwoord-reset: wachtwoord zetten. */
export async function setPasswordAsInvitedKlant (password: string) {
  await setPasswordAndClearInvitePending(password)
}

export async function requestKlantPasswordReset (email: string) {
  const trimmed = email.trim()
  if (!trimmed) throw new Error('E-mail is verplicht')
  const redirectTo =
    typeof window !== 'undefined'
      ? `${window.location.origin}/klant/wachtwoord-reset`
      : undefined
  const { error } = await supabase.auth.resetPasswordForEmail(trimmed, { redirectTo })
  if (error) {
    throw new Error(error.message ?? 'Kon geen resetmail sturen')
  }
}

export async function signOut() {
  try {
    clearInvitePasswordPendingHint()
    await supabase.auth.signOut({ scope: 'local' })
  } catch {
    // 403 of netwerkfout: negeer zodat de UI toch uitlogt (AuthContext zet profile op null)
  }
}

export function subscribeAuth (
  callback: (user: User | null, event: AuthChangeEvent) => void
) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    void supabase.realtime.setAuth(session?.access_token ?? null)
    callback(session?.user ?? null, event)
  })
  return () => {
    subscription.unsubscribe()
  }
}
